import React from "react";
import { Link } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CheckCircle2, AlertCircle, ArrowRight, Loader2 } from "lucide-react";

export function Field({ id, label, required, error, hint, children }) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id} className="text-sm font-medium text-[#0A1628]">
        {label}{required && <span className="text-[#1A6EFF] ml-0.5" aria-hidden="true">*</span>}
        {!required && <span className="text-muted-foreground font-normal ml-1 text-xs">(optional)</span>}
      </Label>
      {children}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
      {error && <p id={`${id}-error`} role="alert" data-testid={`${id}-error`} className="text-xs text-red-600 flex items-center gap-1"><AlertCircle className="h-3.5 w-3.5" />{error}</p>}
    </div>
  );
}

export function TextField({ id, label, required, error, value, onChange, type = "text", placeholder, hint, autoComplete }) {
  return (
    <Field id={id} label={label} required={required} error={error} hint={hint}>
      <Input id={id} name={id} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        aria-required={required} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined} autoComplete={autoComplete}
        data-testid={`${id}-input`} className={`h-12 bg-white ${error ? "border-red-400 focus-visible:ring-red-400" : ""}`} />
    </Field>
  );
}

export function TextAreaField({ id, label, required, error, value, onChange, placeholder, rows = 5 }) {
  return (
    <Field id={id} label={label} required={required} error={error}>
      <Textarea id={id} name={id} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        aria-required={required} aria-invalid={!!error} aria-describedby={error ? `${id}-error` : undefined}
        data-testid={`${id}-input`} className={`bg-white ${error ? "border-red-400" : ""}`} />
    </Field>
  );
}

export function SelectField({ id, label, required, error, value, onChange, options, placeholder = "Select…" }) {
  return (
    <Field id={id} label={label} required={required} error={error}>
      <Select value={value} onValueChange={onChange} name={id}>
        <SelectTrigger id={id} data-testid={`${id}-select`} aria-required={required} aria-invalid={!!error} className={`h-12 bg-white ${error ? "border-red-400" : ""}`}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>{options.map((o) => <SelectItem key={o} value={o} data-testid={`${id}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </Field>
  );
}

export function Honeypot({ value, onChange }) {
  return (
    <div className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" aria-hidden="true">
      <label htmlFor="website">Website</label>
      <input id="website" name="website" tabIndex={-1} autoComplete="off" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function Consent({ id = "consent", checked, onChange, error }) {
  return (
    <div>
      <label className="flex items-start gap-3 text-sm text-muted-foreground cursor-pointer">
        <input id={id} name={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} data-testid={`${id}-checkbox`} className="mt-1 h-4 w-4 accent-[#1A6EFF]" />
        <span>I agree that TechAI Fortune may contact me about this request and store my information in line with the <Link to="/privacy-policy" className="text-[#1A6EFF] underline">Privacy Policy</Link>.</span>
      </label>
      {error && <p role="alert" className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export function SubmitRow({ state, label, errors, testId }) {
  return (
    <div className="space-y-3">
      {errors._form && <div role="alert" data-testid="form-error-banner" className="rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm px-4 py-3 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{errors._form}</div>}
      <Button type="submit" disabled={state === "submitting"} data-testid={testId} className="h-12 px-8 rounded-full bg-[#1A6EFF] hover:bg-[#0F5AE0] text-white font-semibold shadow-[0_8px_24px_rgba(26,110,255,0.35)]">
        {state === "submitting" ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending…</> : <>{label} <ArrowRight className="ml-2 h-4 w-4" /></>}
      </Button>
      <p className="text-xs text-muted-foreground">Fields marked <span className="text-[#1A6EFF]">*</span> are required.</p>
    </div>
  );
}

export function SuccessState({ title, desc, onReset, testId }) {
  return (
    <div role="status" data-testid={testId} className="rounded-2xl border border-[#1A6EFF]/20 bg-[#F0F4FF] p-8 text-center fade-up">
      <div className="mx-auto h-14 w-14 rounded-full bg-[#1A6EFF] text-white grid place-items-center glow-blue"><CheckCircle2 className="h-7 w-7" /></div>
      <h3 className="mt-5 font-display text-2xl font-bold text-[#0A1628]">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-md mx-auto leading-relaxed">{desc}</p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link to="/case-studies"><Button variant="outline" className="rounded-full h-11">Explore case studies</Button></Link>
        <Button variant="ghost" className="rounded-full h-11" onClick={onReset} data-testid="form-reset-btn">Submit another</Button>
      </div>
    </div>
  );
}
